import React, { useState, useEffect } from 'react';
import api from '@/utils/api';
import { Card, CardContent } from '@/components/ui/card';
import { UsersRound, Copy, MailPlus, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Admin-Panel: Kanzlei-Team-Logins (Aug 2026).
 * Mehrere E-Mail-Adressen pro Kanzlei — alle teilen dieselbe Mandantensicht.
 * Neuer Zugang: E-Mail mit Einrichtungs-Link (7 Tage, Single-Use) — Mitglied legt Passwort selbst fest.
 */
export default function KanzleiTeamPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [inviteInfo, setInviteInfo] = useState(null); // { email, setup_url, email_sent }
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/lex/kanzlei-team');
      setItems(res.data.items || []);
    } catch (e) {
      console.error('Kanzlei-Team laden fehlgeschlagen:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const copyLink = (url) => {
    navigator.clipboard.writeText(url).then(() => toast.success('Link kopiert.')).catch(() => {});
  };

  const addMember = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) return;
    setSaving(true);
    try {
      const res = await api.post('/lex/kanzlei-team', { email: email.trim(), name: name.trim() });
      setInviteInfo(res.data);
      setName(''); setEmail('');
      toast.success(res.data.email_sent
        ? `Einrichtungs-Link an ${res.data.email} gesendet.`
        : `Zugang angelegt — Mail-Versand fehlgeschlagen, bitte Link manuell weitergeben.`);
      if (res.data.separate_sb_account) {
        toast.info('Hinweis: Für diese E-Mail existiert zusätzlich ein ivoliq-SB-Konto — beide Logins bleiben getrennt (eigenes Passwort fürs Kanzlei-Portal).');
      }
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Anlegen fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  };

  const resetPassword = async (it) => {
    if (!window.confirm(`Neuen Einrichtungs-Link an ${it.email} senden? Das bisherige Passwort wird ungültig, alle Sitzungen werden abgemeldet.`)) return;
    setBusyId(it.id);
    try {
      const res = await api.post(`/lex/kanzlei-team/${it.id}/reset-password`);
      setInviteInfo(res.data);
      toast.success(res.data.email_sent ? 'Neuer Einrichtungs-Link gesendet.' : 'Link erzeugt — Mail-Versand fehlgeschlagen.');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Zurücksetzen fehlgeschlagen.');
    } finally {
      setBusyId(null);
    }
  };

  const toggleActive = async (it) => {
    const action = it.disabled ? 'reactivate' : 'deactivate';
    const label = it.disabled ? 'reaktivieren' : 'deaktivieren';
    if (!window.confirm(`${it.email} wirklich ${label}?`)) return;
    setBusyId(it.id);
    try {
      await api.post(`/lex/kanzlei-team/${it.id}/${action}`);
      toast.success(it.disabled ? 'Konto reaktiviert.' : 'Konto deaktiviert — Sitzungen beendet.');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Aktion fehlgeschlagen.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Card data-testid="kanzlei-team-panel">
      <CardContent className="pt-5 space-y-4">
        <div>
          <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
            <UsersRound className="h-4 w-4" style={{ color: '#C5A059' }} />
            Kanzlei-Team-Logins (/kanzlei)
          </h3>
          <p className="text-xs text-gray-500 mt-1">
            Neuer Zugang: Das Mitglied erhält eine E-Mail mit Einrichtungs-Link (7 Tage gültig) und legt sein
            Passwort selbst fest. 2FA optional pro Person. Alle sehen dieselben Mandanten.
          </p>
        </div>

        {inviteInfo && (
          <div className="border rounded-lg p-3 bg-emerald-50 border-emerald-300" data-testid="kanzlei-team-invite-info">
            <p className="text-xs font-semibold text-emerald-900 mb-1">
              {inviteInfo.email_sent
                ? `Einrichtungs-Link per E-Mail an ${inviteInfo.email} gesendet.`
                : `Mail-Versand fehlgeschlagen — Link bitte manuell an ${inviteInfo.email} weitergeben:`}
            </p>
            <div className="flex items-center gap-2">
              <code className="text-[11px] font-mono bg-white border border-emerald-200 rounded px-2 py-1 truncate max-w-md" data-testid="kanzlei-team-setup-url">{inviteInfo.setup_url}</code>
              <button onClick={() => copyLink(inviteInfo.setup_url)} className="text-emerald-800 hover:text-emerald-950 shrink-0" title="Link kopieren" data-testid="kanzlei-team-copy-link">
                <Copy className="h-4 w-4" />
              </button>
              <button onClick={() => setInviteInfo(null)} className="text-xs text-emerald-700 underline ml-auto shrink-0" data-testid="kanzlei-team-dismiss-invite">Ausblenden</button>
            </div>
          </div>
        )}

        <form onSubmit={addMember} className="flex flex-wrap items-end gap-2">
          <div>
            <label className="text-xs text-gray-500 block mb-1">Name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} required
              className="border border-gray-300 rounded-md px-3 py-1.5 text-sm w-44"
              placeholder="Dr. Anna Beispiel" data-testid="kanzlei-team-add-name" />
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">E-Mail</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
              className="border border-gray-300 rounded-md px-3 py-1.5 text-sm w-60"
              placeholder="mitarbeiter@kanzlei.at" data-testid="kanzlei-team-add-email" />
          </div>
          <button type="submit" disabled={saving}
            className="text-xs font-semibold px-4 py-2 border transition-colors hover:bg-gray-50 disabled:opacity-50"
            style={{ color: '#0A1128', borderColor: '#C5A059' }} data-testid="kanzlei-team-add-btn">
            {saving ? 'Sende Einladung…' : '+ Zugang einladen'}
          </button>
        </form>

        {loading ? (
          <p className="text-sm text-gray-500 py-2" data-testid="kanzlei-team-loading">Lade Kanzlei-Team…</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="kanzlei-team-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-gray-500 border-b border-gray-200">
                  <th className="py-2 pr-3">Name</th>
                  <th className="py-2 pr-3">E-Mail</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2 pr-3">2FA</th>
                  <th className="py-2 text-right">Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.id} className={`border-b border-gray-100 ${it.disabled ? 'opacity-50' : ''}`} data-testid={`kanzlei-team-row-${it.email}`}>
                    <td className="py-2 pr-3 font-medium text-gray-900">
                      {it.name || '—'}
                      {it.primary && <span className="ml-2 px-1.5 py-0.5 text-[10px] font-semibold rounded bg-slate-100 text-slate-700">Hauptkonto</span>}
                    </td>
                    <td className="py-2 pr-3 text-gray-700">{it.email}</td>
                    <td className="py-2 pr-3">
                      {it.disabled ? (
                        <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-rose-100 text-rose-700">Deaktiviert</span>
                      ) : it.setup_pending ? (
                        <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-amber-100 text-amber-800">Einladung offen</span>
                      ) : it.must_change_password ? (
                        <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-amber-100 text-amber-800">Erstlogin offen</span>
                      ) : (
                        <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-emerald-100 text-emerald-800">Aktiv</span>
                      )}
                    </td>
                    <td className="py-2 pr-3">
                      {it.totp_enabled
                        ? <span className="inline-flex items-center gap-1 text-emerald-700 text-xs"><ShieldCheck className="h-3.5 w-3.5" /> an</span>
                        : <span className="text-xs text-gray-400">aus</span>}
                    </td>
                    <td className="py-2 text-right space-x-2 whitespace-nowrap">
                      {/* „Neuer Link" auch fürs Hauptkonto (Sep 2026): normales Anmeldeverfahren
                          (eigenes Passwort via Setup-Link + 2FA) statt Initial-Passwort */}
                      <button onClick={() => resetPassword(it)} disabled={busyId === it.id}
                        className="inline-flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900 underline"
                        data-testid={`kanzlei-team-reset-${it.email}`}>
                        <MailPlus className="h-3 w-3" /> Neuer Link
                      </button>
                      {!it.primary && (
                        <button onClick={() => toggleActive(it)} disabled={busyId === it.id}
                          className={`text-xs underline ${it.disabled ? 'text-emerald-700' : 'text-rose-600'}`}
                          data-testid={`kanzlei-team-toggle-${it.email}`}>
                          {it.disabled ? 'Reaktivieren' : 'Deaktivieren'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
